import type { CSSProperties } from 'react';

/**
 * Frame COLOUR as swatches rather than words (owner 2026-07-28, round 2).
 *
 * A colour is something you look at, and the text toggle it replaces made the
 * customer read two words to make a purely visual choice.
 *
 * Callers must only render this once a frame is actually chosen. A colour
 * selector sitting above "No frame (print only)" reads as a decision that has to
 * be made before you can move on, which is exactly wrong for the customer who
 * does not want a frame at all.
 *
 * The hex lives here rather than in pricing because a swatch is presentation, not
 * catalogue data: a new colour needs a pricing edit for the SKU and ONE line here
 * for the chip. An unmapped colour still renders (mid grey) instead of vanishing,
 * so a pricing-only addition degrades to a usable control rather than a gap.
 */
const SWATCH_HEX: Record<string, string> = {
  black: '#1A1A1A',
  white: '#F4F4F4',
  oak: '#C9A66E',
};

export interface FrameColourOption {
  id: string;
  label: string;
}

interface FrameColourPickerProps {
  colours: FrameColourOption[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
}

export function FrameColourPicker({
  colours,
  value,
  onChange,
  className,
}: FrameColourPickerProps) {
  // One colour is not a choice — rendering a single swatch invites a tap that
  // does nothing.
  if (colours.length < 2) return null;
  return (
    <div
      className={`fcol${className ? ` ${className}` : ''}`}
      role="radiogroup"
      aria-label="Frame colour"
    >
      <span className="fcol__label">Frame Colour</span>
      {colours.map((c) => (
        <button
          key={c.id}
          type="button"
          role="radio"
          aria-checked={c.id === value}
          aria-label={c.label}
          title={c.label}
          className={`fcol__sw${c.id === value ? ' is-on' : ''}`}
          style={{ '--fcol': SWATCH_HEX[c.id] ?? '#9A9A9A' } as CSSProperties}
          onClick={() => onChange(c.id)}
        />
      ))}
    </div>
  );
}
